import {
  BroadcastManyQuery,
  BroadcastResourcesInput,
  EnumBroadcastResourcesFormMode,
  EnumBroadcastResourcesType,
  EnumBroadcastStatus,
  useBroadcastCreateOneMutation,
  useBroadcastUpdateByIdMutation,
} from "gql/generated";
import i18n, { i18nNamespace, loadLanguagePacks } from "i18n";
import { useCallback, useMemo } from "react";
import { useFormHandler, useLoggedInAs, usePostHog } from "shared/hooks";
import {
  createMultilingual,
  getMultilingualString,
  updateMultilingual,
} from "shared/utils";
import { z } from "zod";
import {
  createEditResourceFormSchema,
  CreateEditResourceFormValues,
} from "../AddEditResourceFlyup/useAddEditResourceForm";
import _ from "lodash";

// Ensure the required language packs are loaded
await loadLanguagePacks([i18nNamespace.Core, i18nNamespace.Home]);

const createEditBroadcastFormSchema = z.object({
  name: z
    .string({
      required_error: `${i18n.t("home:VIEWS.BROADCAST.MODAL.DETAILS.FORM.NAME")} ${i18n.t("core:FORM_ERRORS.REQUIRED")}`,
    })
    .min(1, {
      message: `${i18n.t("home:VIEWS.BROADCAST.MODAL.DETAILS.FORM.NAME")} ${i18n.t("core:FORM_ERRORS.REQUIRED")}`,
    }),
  description: z
    .string()
    .max(100, {
      message: `${i18n.t("home:VIEWS.BROADCAST.MODAL.COLLECTION_INFO.FORM.DESCRIPTION")} ${i18n.t("core:FORM_ERRORS.MAX_CHARACTERS", { count: 100 })}`,
    })
    .optional(),
  resources: z.array(createEditResourceFormSchema).optional(),
  coverImage: z.string().optional(),
  publicName: z.string().optional(),
});

export type CreateEditBroadcastFormValues = z.infer<
  typeof createEditBroadcastFormSchema
>;

interface CreateEditBroadcastFormOptions {
  onSuccess?: (id: string) => void;
  onError?: (message: string) => void;
  broadcast?: BroadcastManyQuery["broadcastMany"][0];
}

export const useCreateEditBroadcastForm = ({
  onSuccess,
  onError,
  broadcast,
}: CreateEditBroadcastFormOptions) => {
  const defaultValues: CreateEditBroadcastFormValues = useMemo(
    () => ({
      name: getMultilingualString(broadcast?.name ?? {}),
      description: getMultilingualString(broadcast?.description ?? {}),
      resources: broadcast?.resources
        ? _.compact(broadcast.resources).map(r => ({
            title: getMultilingualString(r.title ?? {}),
            description: getMultilingualString(r.description ?? {}),
            message: getMultilingualString(r.message ?? {}),
            type: r.type,
            coverImage: r.coverImage?._id,
            file: r.file?._id,
            video: r.video?._id,
            videoURL: r.videoURL ?? undefined,
            photos: r.photos ? r.photos.map(p => p._id) : [],
            course: r.course ?? undefined,
            iframeUrl: r?.iframeUrl ?? undefined,
            survey: (r as { survey?: { _id?: string } })?.survey?._id ?? "",
            formMode: (r as { formMode?: string })?.formMode ?? "ONE_AND_DONE",
            allowSaveAndEdit: (r as { allowSaveAndEdit?: boolean })?.allowSaveAndEdit ?? false,
            requireApproval: (r as { requireApproval?: boolean })?.requireApproval ?? false,
            allowResubmission: (r as { allowResubmission?: boolean })?.allowResubmission ?? false,
            completionTags: (r as { completionTags?: string[] })?.completionTags ?? [],
            submissionTags: (r as { submissionTags?: string[] })?.submissionTags ?? [],
            booker: (r as { booker?: { _id?: string } })?.booker?._id ?? "",
            teamVisible: (r as { teamVisible?: boolean })?.teamVisible ?? false,
            ctaLabel: getMultilingualString(r.ctaLabel ?? {}),
            _id: r._id,
          }))
        : [],
      coverImage: broadcast?.coverImage?._id ?? "",
      publicName: getMultilingualString(broadcast?.publicName ?? {}),
    }),
    [broadcast],
  );

  const loggedInAs = useLoggedInAs();
  const { trackPostHogException } = usePostHog();

  const [createBroadcast] = useBroadcastCreateOneMutation();
  const [updateBroadcast] = useBroadcastUpdateByIdMutation();

  return useFormHandler<CreateEditBroadcastFormValues>(
    useCallback(
      async data => {
        try {
          if (data) {
            const newResources = data.resources
              ? _.compact(data.resources).filter(r => !!r.type)
              : [];
            const formattedResources = getFormattedResourcesToSave(
              newResources as CreateEditResourceFormValues[],
              broadcast?.resources ?? [],
            );
            // If we are editing, we need to update the broadcast
            if (broadcast?._id) {
              const result = await updateBroadcast({
                variables: {
                  id: broadcast._id,
                  record: {
                    name: updateMultilingual(broadcast.name, data.name ?? ""),
                    description: data.description
                      ? updateMultilingual(
                          broadcast.description ?? {},
                          data.description,
                        )
                      : undefined,
                    resources: formattedResources,
                    coverImage: data.coverImage,
                    publicName: data.publicName
                      ? updateMultilingual(
                          broadcast.publicName ?? {},
                          data.publicName,
                        )
                      : undefined,
                  },
                },
              });
              if (result.data?.broadcastUpdateById?.record?._id) {
                onSuccess?.(result.data?.broadcastUpdateById?.record?._id);
              } else {
                throw new Error(result.errors?.[0]?.message);
              }
            } else {
              const result = await createBroadcast({
                variables: {
                  record: {
                    name: createMultilingual(data.name ?? ""),
                    description: data.description
                      ? createMultilingual(data.description)
                      : undefined,
                    owner: {
                      entityId: loggedInAs?.entityId,
                      type: loggedInAs?.type,
                    },
                    status: EnumBroadcastStatus.Draft,
                    resources: formattedResources,
                    publicName: data.publicName
                      ? createMultilingual(data.publicName)
                      : undefined,
                  },
                },
              });
              if (result.data?.broadcastCreateOne?.record?._id) {
                onSuccess?.(result.data?.broadcastCreateOne?.record?._id);
              } else {
                throw new Error(result.errors?.[0]?.message);
              }
            }
          }
        } catch (error) {
          trackPostHogException(error, {
            fileName: "useCreateEditBroadcastForm.ts",
            method: "onSubmit",
            message: "Error creating/editing broadcast",
          });
          onError?.(data?.name ?? "");
        }
      },
      [
        broadcast?.resources,
        broadcast?._id,
        broadcast?.name,
        broadcast?.description,
        broadcast?.publicName,
        updateBroadcast,
        onSuccess,
        createBroadcast,
        loggedInAs?.entityId,
        loggedInAs?.type,
        onError,
        trackPostHogException,
      ],
    ),
    defaultValues,
    { schema: createEditBroadcastFormSchema },
  );
};

/**
 * This function ensures only the resource fields that are required per
 * resource type are included in each resource object returned in the resources
 * array. This is to ensure no extra fields are included when saving
 * the resources array to the database.
 */
const getFormattedResourcesToSave = (
  resources: CreateEditResourceFormValues[],
  currentResources: BroadcastManyQuery["broadcastMany"][0]["resources"],
): BroadcastResourcesInput[] => {
  return _.compact(resources).map(r => {
    const currentResource = _.compact(currentResources).find(
      c => c._id === r._id,
    );

    const getConditionalFields = () => {
      switch (r.type) {
        case EnumBroadcastResourcesType.Message:
          return {
            message: updateMultilingual(
              currentResource?.message ?? {},
              r.message ?? "",
            ),
          };
        case EnumBroadcastResourcesType.Pdf:
          return {
            file: r.file,
          };
        case EnumBroadcastResourcesType.Video:
          return {
            video: r.video,
            videoURL: r.videoURL,
          };
        case EnumBroadcastResourcesType.Photos:
          return {
            photos: r.photos,
          };
        case EnumBroadcastResourcesType.Course:
          return {
            course: r.course,
          };
        case EnumBroadcastResourcesType.Custom:
          return {
            iframeUrl: r.iframeUrl,
          } as Partial<BroadcastResourcesInput>;
        case EnumBroadcastResourcesType.Forms:
          return {
            survey: r.survey,
            formMode: (r.formMode ?? "ONE_AND_DONE") as EnumBroadcastResourcesFormMode,
            allowSaveAndEdit: r.allowSaveAndEdit ?? false,
            requireApproval: r.requireApproval ?? false,
            allowResubmission: r.allowResubmission ?? false,
            completionTags: r.completionTags ?? [],
            submissionTags: r.submissionTags ?? [],
            teamVisible: r.teamVisible ?? false,
            ctaLabel:
              r.formMode === "MULTI_SUBMISSION" && r.ctaLabel?.trim()
                ? updateMultilingual(
                    currentResource?.ctaLabel ?? {},
                    r.ctaLabel.trim(),
                  )
                : undefined,
          };
        case EnumBroadcastResourcesType.Storefront:
          return {
            booker: r.booker,
            completionTags: r.completionTags ?? [],
            teamVisible: r.teamVisible ?? false,
            ctaLabel: r.ctaLabel?.trim()
              ? updateMultilingual(
                  currentResource?.ctaLabel ?? {},
                  r.ctaLabel.trim(),
                )
              : undefined,
          };
        default:
          return {};
      }
    };
    return {
      _id: /^[a-f0-9]{24}$/i.test(r._id) ? r._id : undefined,
      type: r.type,
      title: updateMultilingual(currentResource?.title ?? {}, r.title),
      description: r.description
        ? updateMultilingual(
            currentResource?.description ?? {},
            r.description ?? "",
          )
        : undefined,
      coverImage: r.coverImage,
      ...getConditionalFields(),
    };
  });
};
